'use client';

import { LngLat } from '@yandex/ymaps3-types';
import { useTranslations } from 'next-intl';
import { useStore } from 'zustand';

import { ShopList } from '@/api/backend/contract/Shop';
import { IconKitCloseCircle } from '@/icon/kit/CloseCircle';
import { storeUiModal } from '@/store-ui/modal';
import { UiMapsPickup } from '@/ui/maps-pickup';
import { UiMapsShop } from '@/ui/maps-shop/UiMapsShop';
import { UiKitDialog } from '@/ui-kit/dialog';
import type { MapBoundsChangePayload } from '@/ui-kit/map-content';
import type { PickupPoint, PickupPointTypeOption } from '@/util/map-point-maps/types';

import { styles } from './UiMapsModal.styles';

export interface UiMapsModalProps {
  childrenBefore?: React.ReactNode;
  childrenAfter?: React.ReactNode;
  isFreeShipping?: boolean;
  center?: [number, number];
  type?: 'pickup' | 'shops';
  modalId: 'mapsModalShops' | 'mapsModalPickup';
  points: PickupPoint[] | ShopList | null;
  city?: string;
  cityLngLat?: LngLat;
  pointTypesOptions?: PickupPointTypeOption[];
  onBoundsChange?: (bounds: MapBoundsChangePayload) => void;
  onSelect?: (pointId: string) => void;
  loadingPoints?: boolean;
}

export function UiMapsModal({
  childrenBefore,
  childrenAfter,
  isFreeShipping,
  type = 'pickup',
  modalId,
  points = [],
  city,
  cityLngLat,
  pointTypesOptions,
  onBoundsChange,
  onSelect,
  loadingPoints = false
}: UiMapsModalProps) {
  const slots = styles({ type });
  const t = useTranslations();

  const modal = useStore(storeUiModal, (state) => state.modals[modalId]);
  const close = useStore(storeUiModal, (state) => state.close);

  if (points === null) return null;

  const onPickupPointSelect = (pointId: string) => {
    onSelect?.(pointId);
  };

  if (!modal?.isOpen) return null;

  return (
    <div className={slots.base()}>
      <UiKitDialog
        open={true}
        onOpenChange={() => close(modalId)}
        title="Title"
        fullScreen
        className="size-full p-0 sm:p-0"
        autoFocus={true}
        customCloseButton={
          <div className={slots.close()}>
            <IconKitCloseCircle
              width={40}
              height={40}
            />
          </div>
        }
      >
        {childrenBefore}
        {type === 'pickup' && (
          <UiMapsPickup
            city={city ? city : t('Moscow')}
            points={points as PickupPoint[]}
            onPointConfirm={onSelect ? onPickupPointSelect : undefined}
            isFreeShipping={isFreeShipping}
            center={cityLngLat}
            pointTypesOptions={pointTypesOptions}
            onBoundsChange={onBoundsChange}
            loading={loadingPoints}
          />
        )}
        {type === 'shops' && (
          <UiMapsShop
            city={t('Moscow')}
            points={points as ShopList}
            onPointConfirm={onPickupPointSelect}
            isFreeShipping={isFreeShipping}
            type="pdp"
          />
        )}
        {childrenAfter}
      </UiKitDialog>
    </div>
  );
}
